import { apiClient } from './client'
import type { ApiResponse } from '../types/api'
import type { Supplier, SupplierQueuePage, SupplierVerificationStatus } from '../types/supplier'
import type { GeoBucket, PlatformSplit } from '../types/dashboard'
import type { SupplyInsightRange, SupplyInsights, SupplierProfileInsights } from '../types/supplyDashboard'

export interface SupplierAdminListParams {
  verificationStatus?: SupplierVerificationStatus
  search?: string
  cursor?: string
  limit?: number
  /** 1-based page: numbered paging with a total (needs an API from 3 Nov 2026 on). */
  page?: number
}

export interface SupplierUpdateInput {
  plan?: string
  categories?: string[]
  businessName?: string
  contactDetails?: {
    phoneNumber?: string | null
    whatsappNumber?: string | null
    email?: string | null
    websiteUrl?: string | null
  }
  legalDetails?: {
    countryOfIncorporation: string
    tin?: string
    companyRegistrationNumber?: string
    governmentVerificationBureau?: string
  }
  location?: {
    line1: string
    line2?: string
    country: string
    city: string
    region: string
    district: string
  }
}

export interface SupplierAdminStats {
  suppliers: {
    total: number
    byStatus: Record<SupplierVerificationStatus, number>
    byCountry: { country: string; count: number }[]
  }
  products: {
    total: number
    popular: number
    byCategory: { categoryId: string; name?: { en: string }; count: number }[]
  }
  plans: { planId: string; name?: { en: string }; count: number }[]
}

/** Counts for the cards above the Suppliers table. "Unknown" marks a missing location. */
export interface SupplierInsights {
  total: number
  /** From the business address (`location.country`). */
  byCountry: GeoBucket[]
  /** From the legal details (`legalDetails.countryOfIncorporation`). */
  byIncorporationCountry: GeoBucket[]
  /** `code` carries the city's country. */
  byCity: GeoBucket[]
  /** Every active category, including ones with no suppliers yet. */
  byCategory: GeoBucket[]
  byPlan: GeoBucket[]
  /** Pending → Verified → Rejected → Unverified, of the filtered set. */
  byStatus: GeoBucket[]
  /**
   * TZS-only (no FX), not-taken-down listings of suppliers matching the filter — a live snapshot.
   * Money: missing for admins not on ADMIN_CONTROL_ALLOWED_EMAILS (Todd, 3 Nov 2026).
   */
  catalogueValue?: { valueTzs: number; products: number }
  /** How many products each supplier (matching the filter) has listed, bucketed. */
  productsPerSupplier: GeoBucket[]
}

export type SupplierApplicationStep = 'plan' | 'category' | 'basics' | 'legal' | 'review'

/** GET /suppliers/admin/applications/insights — website vs app and the web application funnel. */
export interface SupplierApplicationInsights {
  range: SupplyInsightRange
  periodStart: string | null
  periodEnd: string
  /** First day web/app was recorded; older profiles are "Before tracking". */
  trackedFrom: string | null
  /** Supplier profiles created in the window. */
  source: PlatformSplit
  /** Today's review queue, by where each profile was created. Not windowed. */
  pendingBySource: PlatformSplit
  funnel: {
    started: number
    startedByPlatform: PlatformSplit
    steps: { step: SupplierApplicationStep; label: string; reached: number; dropOff: number; dropOffPct: number | null }[]
    submitted: number
    conversionPct: number | null
    /** All unsubmitted applications, not just this window's. */
    incomplete: number
  }
  /** Median hours; null with nothing to measure. */
  timeToSubmit: {
    fromSignupHours: { all: number | null; web: number | null; app: number | null }
    fromApplicationStartHours: number | null
  }
}

export interface IncompleteApplication {
  userId: string
  fullName: string | null
  username: string | null
  phoneNumber: string | null
  email: string | null
  furthestStep: SupplierApplicationStep | null
  furthestStepNumber: number
  totalSteps: number
  /** e.g. "3 of 5: Basics". */
  furthestStepLabel: string
  platform: 'web' | 'app'
  startedAt: string
  lastActivityAt: string
}

export interface IncompleteApplicationsPage {
  items: IncompleteApplication[]
  page: number
  limit: number
  total: number
}

export const suppliersApi = {
  getApplicationInsights: (params: { range: SupplyInsightRange; from?: string; to?: string }) =>
    apiClient.get<ApiResponse<SupplierApplicationInsights>>('/suppliers/admin/applications/insights', { params }),

  /** Applicants' contact details: "edit" on Suppliers (Supplier role and up). */
  listIncompleteApplications: (params: { page: number; limit: number }) =>
    apiClient.get<ApiResponse<IncompleteApplicationsPage>>('/suppliers/admin/applications/incomplete', { params }),

  list: (params: SupplierAdminListParams) =>
    apiClient.get<ApiResponse<SupplierQueuePage>>('/suppliers/admin/queue', { params }),

  getInsights: (verificationStatus?: SupplierVerificationStatus) =>
    apiClient.get<ApiResponse<SupplierInsights>>('/suppliers/admin/insights', { params: { verificationStatus } }),

  getStats: () => apiClient.get<ApiResponse<SupplierAdminStats>>('/suppliers/admin/stats'),

  /** Windowed tiles/chart/ranked-lists for the Supply Dashboard page. */
  getStatsWindowed: (params: { range: SupplyInsightRange; from?: string; to?: string }) =>
    apiClient.get<ApiResponse<SupplyInsights>>('/suppliers/admin/stats/windowed', { params }),

  getById: (id: string) =>
    apiClient.get<ApiResponse<Supplier>>(`/suppliers/admin/${id}`),

  /** One supplier's Supply Dashboard figures, for its profile page (restricted figures stripped server-side). */
  getSupplierInsights: (id: string, params: { range: SupplyInsightRange; from?: string; to?: string }) =>
    apiClient.get<ApiResponse<SupplierProfileInsights>>(`/suppliers/admin/${id}/insights`, { params }),

  setVerification: (
    id: string,
    verificationStatus: Extract<SupplierVerificationStatus, 'verified' | 'rejected' | 'unverified'>,
    rejectionReason?: string,
  ) =>
    apiClient.patch<ApiResponse<Supplier>>(`/suppliers/admin/${id}/verification`, {
      verificationStatus,
      rejectionReason,
    }),

  update: (id: string, data: SupplierUpdateInput) =>
    apiClient.patch<ApiResponse<Supplier>>(`/suppliers/admin/${id}`, data),

  delete: (id: string) => apiClient.delete<ApiResponse<void>>(`/suppliers/admin/${id}`),

  setSuspended: (id: string, isSuspended: boolean, reason?: string) =>
    apiClient.patch<ApiResponse<Supplier>>(`/suppliers/admin/${id}/suspend`, {
      isSuspended,
      reason,
    }),
}
